import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const MENU_ITEMS = [
  { label: "My Account",    Icon: PersonOutlinedIcon,                 path: "/account/profile" },
  { label: "My Orders",     Icon: ShoppingBagOutlinedIcon,            path: "/account/orders" },
  { label: "Track Order",   Icon: LocalShippingOutlinedIcon,          path: "/account/track" },
  { label: "My List",       Icon: FavoriteBorderIcon,                 path: "/account/wishlist" },
  { label: "Wallet",        Icon: AccountBalanceWalletOutlinedIcon,   path: "/account/wallet" },
  { label: "Referral",      Icon: CardGiftcardOutlinedIcon,           path: "/account/referral" },
  { label: "My Address",    Icon: LocationOnOutlinedIcon,             path: "/account/addresses" },
  { label: "Notifications", Icon: NotificationsNoneOutlinedIcon,      path: "/account/notifications" },
];

const UserProfile = ({ isAuthenticated, user, onLogout, onLogin }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Not logged in: Login button ── */
  if (!isAuthenticated) {
    return (
      <button
        onClick={onLogin}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-primary-600/25 transition-all hover:bg-primary-700 hover:shadow-xl hover:shadow-primary-600/30 active:scale-95 lg:w-auto"
      >
        <PersonOutlinedIcon style={{ fontSize: 18 }} />
        <span>Login</span>
      </button>
    );
  }

  /* ── Logged in: avatar + dropdown ── */
  const initials = user?.name
    ? user.name.slice(0, 2).toUpperCase()
    : user?.mobile
    ? user.mobile.slice(-2)
    : "U";

  return (
    <div ref={ref} className="relative">
      {/* Avatar trigger */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={[
          "flex items-center gap-2.5 rounded-2xl px-3 py-2 transition-all duration-200",
          open
            ? "bg-primary-50 ring-2 ring-primary-500/30 shadow-md"
            : "hover:bg-gray-50 hover:shadow-sm",
        ].join(" ")}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {/* Avatar circle */}
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-sm font-extrabold text-white shadow-md shadow-primary-600/30">
          {initials}
        </div>

        {/* Name on desktop */}
        <div className="hidden flex-col text-left lg:flex">
          <span className="text-[10px] font-bold uppercase tracking-widest text-primary-600">
            Account
          </span>
          <span className="text-[13px] font-bold text-gray-800 leading-tight">
            {user?.name || user?.mobile || "Profile"}
          </span>
        </div>

        <ExpandMoreIcon
          className={`hidden text-gray-400 transition-transform duration-200 lg:block ${open ? "rotate-180" : ""}`}
          style={{ fontSize: 18 }}
        />
      </button>

      {/* ── Dropdown panel ── */}
      {open && (
        <div className="absolute right-0 top-[calc(100%+10px)] z-50 w-64 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-black/10">

          {/* User info header */}
          <div className="border-b border-gray-100 bg-gradient-to-br from-primary-50 to-white px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-base font-extrabold text-white shadow-md">
                {initials}
              </div>
              <div className="min-w-0">
                {user?.name && (
                  <p className="truncate font-bold text-gray-900">{user.name}</p>
                )}
                <p className="truncate text-sm text-gray-500">
                  {user?.mobile || user?.email || ""}
                </p>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-2">
            {MENU_ITEMS.map(({ label, Icon, path }) => (
              <button
                key={label}
                onClick={() => { navigate(path); setOpen(false); }}
                className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm font-semibold text-gray-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
              >
                <Icon style={{ fontSize: 18 }} className="shrink-0 text-gray-400" />
                <span className="flex-1">{label}</span>
                <ChevronRightIcon style={{ fontSize: 16 }} className="text-gray-300" />
              </button>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 px-3 py-2.5">
            <button
              onClick={() => { onLogout(); setOpen(false); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-red-500 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <LogoutOutlinedIcon style={{ fontSize: 18 }} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
